import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
    const { t } = useI18n();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setLoading(true);
        try { await api.post("/auth/reset-password", { token, password }); setDone(true); }
        catch (err) { setError(formatApiError(err)); } finally { setLoading(false); }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-secondary/30 px-6">
            <div className="w-full max-w-md rounded-3xl border border-border/70 bg-card p-8">
                <div className="mb-8 flex items-center justify-between"><Logo /><LanguageSwitcher compact /></div>
                {done ? (
                    <div className="text-center" data-testid="reset-done">
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent/12 text-accent"><CheckCircle2 className="h-7 w-7" /></div>
                        <h1 className="mt-4 font-serif text-2xl font-bold">{t("auth.resetDone")}</h1>
                        <Button onClick={() => nav("/login")} className="mt-6 rounded-full" data-testid="reset-go-login">{t("common.signIn")}</Button>
                    </div>
                ) : !token ? (
                    <div className="text-center">
                        <h1 className="font-serif text-2xl font-bold">{t("auth.resetTitle")}</h1>
                        <p className="mt-2 text-sm text-destructive">{t("auth.resetInvalid")}</p>
                        <Link to="/forgot-password"><Button variant="outline" className="mt-6 rounded-full">{t("auth.forgotTitle")}</Button></Link>
                    </div>
                ) : (
                    <>
                        <h1 className="font-serif text-2xl font-bold">{t("auth.resetTitle")}</h1>
                        <p className="mt-2 text-sm text-muted-foreground">{t("auth.resetSub")}</p>
                        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
                            <div><Label>{t("auth.newPassword")}</Label><Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" className="mt-1.5" /></div>
                            {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
                            <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="reset-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.updatePassword")}</Button>
                        </form>
                        <Link to="/login" className="mt-6 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="mr-1 h-4 w-4" />{t("auth.backToLogin")}</Link>
                    </>
                )}
            </div>
        </div>
    );
}
